/* iter55g24 — World Builder Mode dialog.
 *
 * Opens when the writer switches to "World-Builder" mode from the
 * top-menu picker. Editable 4-axis panel:
 *   • Proximity Layers    — inner/middle/outer stakes
 *   • Mechanics of Conflict
 *   • Tone
 *   • Integration         — motifs / recurring symbols
 *
 * "Deduce from manuscript" runs Claude across the drafted chapters
 * and returns SUGGESTED values. The writer sees a per-field diff
 * (current vs suggested) and can accept ONE field at a time — never
 * a silent overwrite.
 *
 * The "Map" tab shows a recharts RadarChart visualising how developed
 * each axis is (text-length based score, 0-100).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Globe2, Loader2, Sparkles, Save, Check, X, Map, BookOpen,
} from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer, Tooltip as RechartTooltip,
} from 'recharts';
import { AiLoadingOverlay } from '../ui/AiLoadingOverlay';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { streamSSE } from '@/lib/auth_api';
import { DraftableField } from '@/components/ui/DraftableField';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const AXES = [
  { key: 'proximity_layers',     label: 'Proximity Layers',      shortLabel: 'Proximity' },
  { key: 'mechanics_of_conflict', label: 'Mechanics of Conflict', shortLabel: 'Conflict'  },
  { key: 'tone',                 label: 'Tone',                  shortLabel: 'Tone'       },
  { key: 'integration',          label: 'Integration',           shortLabel: 'Integration'},
];

// iter-arc1: Factual world-detail fields populated by the Bible Inference Engine.
const DETAIL_FIELDS = [
  { key: 'setting',      label: 'Setting',       rows: 3 },
  { key: 'era',          label: 'Era / Period',  rows: 2 },
  { key: 'magic_system', label: 'Magic / Tech System', rows: 3 },
  { key: 'society',      label: 'Society & Power', rows: 3 },
  { key: 'geography',    label: 'Key Locations',  rows: 3 },
];

// Score a field 0-100 based on how much text has been entered.
const scoreAxis = (text) => {
  const len = (text || '').trim().length;
  if (len === 0) return 0;
  return Math.min(100, Math.round((len / 150) * 100));
};

export default function WorldBuilderDialog({ open, onClose, projectId }) {
  const [fields, setFields]             = useState({});
  const [worldDetail, setWorldDetail]   = useState({});
  const [detailHints, setDetailHints]   = useState({});
  const [hints, setHints]               = useState({});
  const [suggested, setSuggested]       = useState(null);
  const [loading, setLoading]           = useState(false);
  const [busy, setBusy]                 = useState(false);
  const [saving, setSaving]             = useState(false);
  const [autofilledAt, setAutofilledAt] = useState(null);
  const [inferredAt, setInferredAt]     = useState(null);
  const [activeTab, setActiveTab]       = useState('edit');
  const [wbDrafts, setWbDrafts]         = useState({});

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/projects/${projectId}/world-builder`, {
        withCredentials: true,
      });
      setFields(r.data?.fields || {});
      setWorldDetail(r.data?.world_detail || {});
      setHints(r.data?.hints || {});
      setDetailHints(r.data?.detail_hints || {});
      setAutofilledAt(r.data?.autofilled_at || null);
      setInferredAt(r.data?.inferred_at || null);
    } catch {
      /* soft-fail — writer sees an empty form */
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open) {
      setSuggested(null);
      setActiveTab('edit');
      load();
      // Load drafts
      if (projectId) {
        axios.get(`${API}/projects/${projectId}/bible-drafts`, { withCredentials: true })
          .then((r) => setWbDrafts(r.data.drafts || {}))
          .catch(() => {});
      }
    }
  }, [open, load]);

  // Auto-populate: after loading, if all World Builder fields are empty, auto-deduce
  const [autoDeduceRan, setAutoDeduceRan] = React.useState(false);
  useEffect(() => {
    if (!open || loading || autoDeduceRan || !projectId) return;
    const sessionKey = `loom:world-builder-autofill:${projectId}`;
    if (sessionStorage.getItem(sessionKey)) return;
    const allEmpty = AXES.every((a) => !(fields[a.key] || '').trim());
    if (!allEmpty) return;
    sessionStorage.setItem(sessionKey, '1');
    setAutoDeduceRan(true);
    toast.info('World Builder is empty — AI is deducing from your manuscript…');
    deduce();
  }, [open, loading, fields, projectId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Draft persistence handlers
  const saveWbDraft = useCallback(async (axisKey, axisLabel, draftValue) => {
    if (!projectId) return;
    try {
      const r = await axios.put(`${API}/projects/${projectId}/bible-drafts`,
        { field_key: axisKey, draft_value: draftValue, field_label: axisLabel },
        { withCredentials: true },
      );
      setWbDrafts(r.data.drafts || {});
    } catch { /* soft fail */ }
  }, [projectId]);

  const applyWbDraft = useCallback(async (axisKey) => {
    if (!projectId) return;
    try {
      const r = await axios.post(`${API}/projects/${projectId}/bible-drafts/${axisKey}/apply`, {}, { withCredentials: true });
      setWbDrafts(r.data.drafts || {});
      setFields((prev) => ({ ...prev, [axisKey]: r.data.applied_value }));
      toast.success('Change added to the build cue.', { description: 'It will be applied on your next Commit & Rebuild.' });
    } catch { toast.error('Could not apply the change.'); }
  }, [projectId]);

  const discardWbDraft = useCallback(async (axisKey) => {
    if (!projectId) return;
    try {
      const r = await axios.delete(`${API}/projects/${projectId}/bible-drafts/${axisKey}`, { withCredentials: true });
      setWbDrafts(r.data.drafts || {});
    } catch { /* soft fail */ }
  }, [projectId]);

  const deduce = async () => {
    if (!projectId) return;
    setBusy(true);
    let final = null;
    let errMsg = '';
    try {
      await streamSSE(
        `/projects/${projectId}/stream/world-builder/deduce`,
        {},
        {
          onEvent: (ev) => {
            if (ev.event === 'done') final = ev.data;
            else if (ev.event === 'error') errMsg = ev.data?.message || 'Deduction failed.';
          },
        },
      );
    } catch (e) {
      errMsg = e?.message || 'Connection dropped.';
    }
    setBusy(false);
    if (final) {
      setSuggested(final?.suggested || {});
      toast.success('Claude deduced world-building signals — review each field below.');
    } else {
      toast.error(errMsg || 'Deduction returned no result.');
    }
  };

  const acceptField = (axis) => {
    if (!suggested) return;
    const value = suggested[axis] || '';
    setFields((prev) => ({ ...prev, [axis]: value }));
    setSuggested((prev) => {
      if (!prev) return prev;
      const { [axis]: _, ...rest } = prev;
      return rest;
    });
  };

  const acceptAll = () => {
    if (!suggested) return;
    setFields((prev) => ({ ...prev, ...suggested }));
    setSuggested({});
    toast.success('All suggested fields accepted.');
  };

  const rejectField = (axis) => {
    setSuggested((prev) => {
      if (!prev) return prev;
      const { [axis]: _, ...rest } = prev;
      return rest;
    });
  };

  const patch = (axis, value) =>
    setFields((prev) => ({ ...prev, [axis]: value }));

  const save = async () => {
    if (!projectId) return;
    setSaving(true);
    try {
      const body = {};
      for (const a of AXES) body[a.key] = String(fields[a.key] || '');
      for (const f of DETAIL_FIELDS) body[f.key] = String(worldDetail[f.key] || '');
      const r = await axios.patch(
        `${API}/projects/${projectId}/world-builder`, body,
        { withCredentials: true },
      );
      setFields(r.data?.fields || fields);
      setWorldDetail(r.data?.world_detail || worldDetail);
      toast.success('World Builder saved.');
      window.dispatchEvent(new CustomEvent('loom:world-builder-saved'));
      onClose?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // Radar chart data — scored 0-100 per axis.
  const radarData = AXES.map((a) => ({
    axis:  a.shortLabel,
    score: scoreAxis(fields[a.key]),
    full:  100,
  }));
  const anyFilled = radarData.some((d) => d.score > 0);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="flex max-h-[90vh] w-[min(95vw,56rem)] max-w-[56rem] flex-col overflow-hidden"
        data-testid="world-builder-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe2 className="h-4 w-4 text-primary" />
            World Builder
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            Edit the four world-building axes below, or let Claude deduce
            them from your drafted manuscript and review each field one
            at a time.
          </DialogDescription>
        </DialogHeader>

        {/* ── Toolbar — always visible ── */}
        <div className="flex flex-wrap items-center gap-2 border-y border-panel-border bg-panel-header/40 px-2 py-1.5">
          <Button
            size="sm"
            onClick={deduce}
            disabled={busy || loading}
            data-testid="world-builder-deduce-btn"
            className="gap-1"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            {busy ? 'Reading manuscript…' : 'Deduce from manuscript'}
          </Button>
          {suggested && Object.keys(suggested).length > 0 && (
            <span className="ml-2 rounded-sm bg-emerald-500/15 px-2 py-0.5 text-[10.5px] text-emerald-700 dark:text-emerald-300">
              {Object.keys(suggested).length} field(s) awaiting review
            </span>
          )}
          {suggested && Object.keys(suggested).length > 1 && (
            <Button
              size="sm"
              variant="secondary"
              onClick={acceptAll}
              data-testid="world-builder-accept-all-btn"
              className="gap-1 ml-auto"
            >
              <Check className="h-3 w-3" /> Accept All
            </Button>
          )}
        </div>

        {/* ── Tab layout ── */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-1 flex-col overflow-hidden">
          <TabsList className="mx-3 mt-2 w-fit shrink-0">
            <TabsTrigger value="edit" className="gap-1.5 text-[12px]" data-testid="world-builder-tab-edit">
              Edit
            </TabsTrigger>
            <TabsTrigger value="story-world" className="gap-1.5 text-[12px]" data-testid="world-builder-tab-story-world">
              <BookOpen className="h-3 w-3" /> Story World
            </TabsTrigger>
            <TabsTrigger value="map" className="gap-1.5 text-[12px]" data-testid="world-builder-tab-map">
              <Map className="h-3 w-3" /> Narrative Map
            </TabsTrigger>
          </TabsList>

          {/* ── EDIT tab ── */}
          <TabsContent value="edit" className="relative flex-1 space-y-3 overflow-y-auto p-3 pb-6 mt-0" data-testid="world-builder-edit-pane">
            <AiLoadingOverlay
              visible={busy}
              message="Claude is reading the manuscript and deducing world elements…"
            />
            {autofilledAt && (
              <div className="rounded-md border border-blue-500/30 bg-blue-500/5 px-3 py-2 text-[11px] text-blue-700 dark:text-blue-400" data-testid="world-builder-autofill-banner">
                Fields below were auto-filled from your Novel Analysis. Edit freely — save to keep.
              </div>
            )}
            {loading && (
              <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
              </div>
            )}
            {!loading && AXES.map((a) => {
              const suggestion = suggested?.[a.key] || '';
              return (
                <div key={a.key} data-testid={`world-builder-field-${a.key}`}>
                  {hints[a.key] && (
                    <div className="mb-1 text-[10.5px] italic text-muted-foreground">
                      {hints[a.key]}
                    </div>
                  )}
                  <DraftableField
                    label={a.label}
                    committedValue={fields[a.key]}
                    draftValue={wbDrafts[a.key]}
                    rows={3}
                    placeholder={hints[a.key] || a.label}
                    onDraftSave={(v) => {
                      patch(a.key, v);
                      saveWbDraft(a.key, a.label, v);
                    }}
                    onApply={() => applyWbDraft(a.key)}
                    onDiscard={() => discardWbDraft(a.key)}
                    testId={`world-builder-input-${a.key}`}
                  />
                  {suggestion && (
                    <div
                      className="mt-1 rounded-sm border border-emerald-500/40 bg-emerald-500/5 p-2"
                      data-testid={`world-builder-diff-${a.key}`}
                    >
                      <div className="mb-1 flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                        <Sparkles className="h-3 w-3" /> Suggested
                      </div>
                      <div className="mb-2 whitespace-pre-wrap text-[12px] leading-snug">
                        {suggestion}
                      </div>
                      <div className="flex gap-1.5">
                        <Button
                          size="sm" variant="secondary"
                          className="h-6 gap-1 px-2 text-[11px]"
                          onClick={() => acceptField(a.key)}
                          data-testid={`world-builder-accept-${a.key}`}
                        >
                          <Check className="h-3 w-3" /> Accept
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          className="h-6 gap-1 px-2 text-[11px]"
                          onClick={() => rejectField(a.key)}
                          data-testid={`world-builder-reject-${a.key}`}
                        >
                          <X className="h-3 w-3" /> Reject
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </TabsContent>

          {/* ── STORY WORLD tab (inferred factual fields) ── */}
          <TabsContent value="story-world" className="relative flex-1 space-y-3 overflow-y-auto p-3 pb-6 mt-0" data-testid="world-builder-story-world-pane">
            {inferredAt && (
              <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-[11px] text-primary/80" data-testid="world-builder-inferred-banner">
                Populated from synopsis by Claude on {new Date(inferredAt).toLocaleDateString()}. Edit freely and save.
              </div>
            )}
            {!inferredAt && (
              <div className="rounded-md border border-dashed border-panel-border/60 bg-muted/20 px-3 py-2 text-[11px] text-muted-foreground">
                These fields are populated automatically when you import a synopsis with <b>File → Import Synopsis</b> or by clicking <b>Fill all Bibles</b> in the Character Bible. You can also edit them manually below.
              </div>
            )}
            {loading && (
              <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
              </div>
            )}
            {!loading && DETAIL_FIELDS.map((f) => (
              <div key={f.key} data-testid={`world-detail-field-${f.key}`} className="space-y-1">
                <label className="text-[12px] font-semibold text-muted-foreground uppercase tracking-wider">
                  {f.label}
                </label>
                {detailHints[f.key] && (
                  <div className="text-[10.5px] italic text-muted-foreground">
                    {detailHints[f.key]}
                  </div>
                )}
                <textarea
                  rows={f.rows}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-y"
                  value={worldDetail[f.key] || ''}
                  onChange={(e) => setWorldDetail((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  placeholder={detailHints[f.key] || f.label}
                  data-testid={`world-detail-input-${f.key}`}
                />
              </div>
            ))}
          </TabsContent>

          {/* ── MAP tab ── */}          <TabsContent value="map" className="flex flex-1 flex-col items-center justify-center overflow-y-auto p-4 mt-0" data-testid="world-builder-map-pane">
            {loading ? (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            ) : (
              <div className="flex w-full flex-col items-center gap-4">
                <div className="text-center">
                  <p className="text-[13px] font-semibold">Narrative Map</p>
                  <p className="text-[11.5px] text-muted-foreground mt-0.5">
                    Each axis is scored 0–100 based on how much content you've written.
                    Fill in the Edit tab to develop your world.
                  </p>
                </div>
                <div className="h-72 w-full max-w-md" data-testid="world-builder-radar-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData} margin={{ top: 8, right: 24, bottom: 8, left: 24 }}>
                      <PolarGrid stroke="hsl(var(--border))" />
                      <PolarAngleAxis
                        dataKey="axis"
                        tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))', fontWeight: 600 }}
                      />
                      <Radar
                        name="Development"
                        dataKey="score"
                        stroke="hsl(var(--primary))"
                        fill="hsl(var(--primary))"
                        fillOpacity={0.3}
                        dot={{ r: 3, fill: 'hsl(var(--primary))' }}
                      />
                      <RechartTooltip
                        formatter={(v, name) => [`${v}%`, 'Development']}
                        contentStyle={{
                          fontSize: 11,
                          background: 'hsl(var(--popover))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: 6,
                          color: 'hsl(var(--popover-foreground))',
                        }}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
                {!anyFilled && (
                  <p className="text-[11.5px] text-muted-foreground italic text-center">
                    All axes are empty — go to the Edit tab to start building your world.
                  </p>
                )}
                <div className="grid w-full max-w-md grid-cols-2 gap-2">
                  {radarData.map((d) => (
                    <div key={d.axis} className="flex items-center justify-between rounded-md border border-border bg-muted/20 px-3 py-2 text-[12px]">
                      <span className="font-medium">{d.axis}</span>
                      <span className={`font-mono font-semibold ${d.score >= 66 ? 'text-emerald-600 dark:text-emerald-400' : d.score >= 33 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>
                        {d.score}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex flex-wrap gap-2 border-t border-panel-border px-3 pt-3">
          <Button variant="ghost" onClick={onClose} disabled={busy || saving}>Cancel</Button>
          <Button
            onClick={save}
            disabled={busy || saving}
            data-testid="world-builder-save-btn"
            className="gap-1"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
